import { RouteLocationNormalized } from 'vue-router';

/** Query keys that are invite/share metadata, not part of the shared content path. */
const INVITE_URL_META_PARAMS = new Set([
  'inviteId',
  'shareId',
  'contentPath',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  '_ch_incp',
]);

/**
 * Resolves contentPath from a share / deep-link URL.
 * Prefer explicit `?contentPath=...`, otherwise rebuild from pathname + content query
 * (e.g. `/interactive-bible?slideId=1&inviteId=...` → `interactive-bible?slideId=1`).
 */
export const resolveContentPathFromUrl = (url: string | URL): string | null => {
  try {
    const urlObject = typeof url === 'string'
      ? new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://localhost')
      : url;

    const fromQuery = urlObject.searchParams.get('contentPath');
    if (fromQuery) {
      return fromQuery;
    }

    const pathname = urlObject.pathname.replace(/^\//, '');
    if (!pathname) {
      return null;
    }

    const contentParams = new URLSearchParams();
    urlObject.searchParams.forEach((value, key) => {
      if (!INVITE_URL_META_PARAMS.has(key)) {
        contentParams.set(key, value);
      }
    });

    const qs = contentParams.toString();

    return qs ? `${pathname}?${qs}` : pathname;
  } catch {
    return null;
  }
};

/**
 * Same as resolveContentPathFromUrl, but for a Vue Router location
 * (used on mobile-web redirect to ChottuLink).
 */
export const resolveContentPathFromRoute = (route: RouteLocationNormalized): string | null => {
  const fromQuery = route.query.contentPath;
  if (typeof fromQuery === 'string' && fromQuery) {
    return fromQuery;
  }

  const pathname = route.path.replace(/^\//, '');
  if (!pathname) {
    return null;
  }

  const contentParams = new URLSearchParams();
  Object.entries(route.query).forEach(([key, value]) => {
    if (INVITE_URL_META_PARAMS.has(key)) {
      return;
    }

    if (typeof value === 'string') {
      contentParams.set(key, value);
    } else if (Array.isArray(value) && typeof value[0] === 'string') {
      contentParams.set(key, value[0]);
    }
  });

  const qs = contentParams.toString();

  return qs ? `${pathname}?${qs}` : pathname;
};
